import { useEffect, useState } from 'react';

import { Button, DescriptionBar, Grid, InfoMessage, SearchBar } from 'components';
import DeleteModal from './DeleteModal';
import SpaceCraftModal from './SpaceCraftModal';
import RegisterModal from './RegisterModal';
import axios from 'common/AxiosAuth';

import { SpaceCraftDetailData, SpaceCraftGridData } from './types';
import { manageGridColumns } from './utils';
import { InformationCircleOutlineIcon, PlusOutlineIcon } from 'assets/svgs';
import { Message } from 'types/message';

import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'hooks/useIsMobile';

const ManageSpaceCraft = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [modalData, setModalData] = useState<SpaceCraftDetailData | null>(null);
  const [deleteModalData, setDeleteModalData] = useState<SpaceCraftGridData[] | null>(null);
  const [isRegisterModalOpened, setIsRegisterModalOpened] = useState<boolean>(false);
  const [spaceCraftData, setSpaceCraftData] = useState<SpaceCraftGridData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const pageSize = 10; // 페이징처리 기준
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (searchValue.trim() === '') {
      getSpaceCraft(currentPage);
    } else {
      searchSpaceCraft(searchValue, currentPage);
    }
  }, [currentPage, searchValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue]);

  const searchSpaceCraft = async (query: string, pageNum: number) => {
    try {
      const res = await axios.get('/api/link/search', {
        params: {
          keyword: query,
          page: pageNum - 1, // 0-based
          size: pageSize,
          sort: 'createdDate,DESC',
        },
      });

      const { content = [], totalElements = 0 } = res.data;

      const spaceCraftGridData: SpaceCraftGridData[] = content.map((item: any) => ({
        name: item.name,
        noradId: item.noradId,
        tleTitle: item.tleTitle,
        // centerFrequency: item.centerFrequency,
        createdDate: item.createdDate,
        direction: item.direction,
        bandName: item.band,
        id: item.linkViewId,
      }));

      setSpaceCraftData(spaceCraftGridData);
      setTotalPages(Math.ceil(totalElements / pageSize));
    } catch (error) {
      console.error('검색 실패:', error);
    }
  };


  const getSpaceCraft = async (pageNum: number) => {
    try {
      const res = await axios.get(`/api/link/page/${pageNum}?pageSize=${pageSize}`);
      const { linkList = [], totalElement = 0 } = res.data;

      console.log('Extracted Data:', linkList); // linkList 확인

      const spaceCraftGridData: SpaceCraftGridData[] = linkList.map((item: any) => ({
        name: item.name,
        noradId: item.noradId,
        tleTitle: item.tleTitle,
        // centerFrequency: item.centerFrequency,
        createdDate: item.createdDate,
        direction: item.direction,
        bandName: item.band,
        id: item.linkViewId,
      }));
      console.log('spaceCraftGridData', spaceCraftGridData);
      setSpaceCraftData(spaceCraftGridData);
      // 총 페이지 수 계산
      const totalPages = Math.ceil(totalElement / pageSize);
      setTotalPages(totalPages);
    } catch (error) {
      console.error('Failed to fetch spacecraft data:', error);
    }
  };

  function onClickDetail(data: SpaceCraftGridData) {
    axios.get(`/api/link/${data.id}`).then(res => {
      const resData = res.data;
      const isUplink = resData.direction === 'UPLINK';
      const temp: SpaceCraftDetailData = {
        id: resData.id,
        direction: resData.direction,
        name: resData.name,
        noradId: resData.noradId,
        tleTitle: resData.tleTitle,
        tleFirst: resData.tleFirst,
        tleSecond: resData.tleSecond,
        band: resData.band,
        centerFrequency: (isUplink ? resData.carrierFrequencyMhz : resData.centerFrequency) ?? resData.centerFrequency,
        bitRate: resData.bitRate,
        subFrequency: resData.subFrequency,
        frameSyncWord: resData.frameSyncWord,
        frameSize: resData.frameSize,
        polarizationId: resData.polarizationKey,
        polarizationName: resData.polarizationValue ?? resData.polarization,
        modulationId: resData.modulationKey,
        modulationName: resData.modulationValue ?? resData.operatingMode,
        convolutionalCodeId: resData.convolutionalCodeKey,
        convolutionalCodeName: resData.convolutionalCodeValue,
        rsCodeId: resData.reedSolomonCodeKey,
        rsCodeName: resData.reedSolomonCodeValue,
        pcmCodingId: resData.pcmCodingKey,
        pcmCodingName: resData.pcmCodingValue,
        syn: resData.syn,
        ctl: resData.ctl,
        lts: resData.lts,
        slip: resData.slip,
        other: resData.other,
        createdDate: resData.createdDate,
        updatedDate: resData.updatedDate,
        pcmCode: resData.pcmCode,
        idlePattern: resData.idlePattern,
        idlePatternLength: resData.idlePatternLength,
        preambleLength: resData.preambleLength,
        carrierFrequencyHz: resData.carrierFrequencyHz,
        carrierFrequencyMhz: resData.carrierFrequencyMhz,
        operatingMode: resData.operatingMode,
        ifCarrierLevel: resData.ifCarrierLevel,
        sweepRate: resData.sweepRate,
        sweepRange: resData.sweepRange,
      };
      setModalData(temp);
    });
  }

  function onClickDelete() {
    setDeleteModalData(checkedRows.map(rowIndex => spaceCraftData[rowIndex]));
  }

  function onClickAddSpacecraft() {
    setIsRegisterModalOpened(true);
  }

  function onClickDeleteButton(index: number) {
    setMessageArray((prev) => prev.filter((message: any) => message.index !== index));
  }

  function onClickUpdate(data: SpaceCraftGridData) {
    console.log('모달', data);
    if (data) {
      axios.get(`/api/link/${data.id}`).then(spacecraftUpdate => {
        // const spacecraftUpdateData = spacecraftUpdate.data;
        // TLE 데이터를 크롤링하는 API 호출
        const noradId = data.noradId; // modalData에서 noradId 추출
        const apiKey = 'LCKJ4S-DRQKKV-R5QRNN-59Q9';
        axios.get(`/rest/${noradId}&apiKey=${apiKey}`).then(res => {
          const resData = res.data;

          // 최신 TLE 데이터 추출
          const tleLines = resData.tle.split('\n');
          const tleFirst = tleLines[0];
          const tleSecond = tleLines[1];

          // ENUM NULL 값이 안되서, 추가로 같이 보냄
          const latestTleData = {
            tleTitle: resData.satname,
            tleFirst: tleFirst,
            tleSecond: tleSecond,
          };
          axios.patch(`/api/link/tle-update/${data.id}`, latestTleData).then(spacecraftRes => {
            console.log('spacecraftRes', spacecraftRes);
            getSpaceCraft(currentPage); // 업데이트 후 데이터를 다시 가져옴
          });
        });
        setMessageArray((prev) => [
          ...prev,
          {
            index: messageArrayCount,
            icon: <InformationCircleOutlineIcon />,
            text: `TLE has been updated.`
          }
        ]);
        setTimeout(() => onClickDeleteButton(messageArrayCount), 2000);
        setMessageArrayCount((prev) => prev + 1);
      }).catch(error => {
        console.error('TLE 데이터 업데이트 실패:', error);
      });
    }
  }

  return (
    <>
      <div className="flex h-full w-full flex-col">
        <DescriptionBar
          className="md:px-6"
          title={t('manage.pageTitle')}
          description={t('manage.pageDescription')}
        />
        <div className="flex flex-row flex-wrap items-center justify-between gap-4 p-6">
          <SearchBar className="w-full md:w-60" value={searchValue} setValue={setSearchValue} placeholder="Search" aria-label="Search spacecraft" />
          <div className="flex w-full flex-row justify-between gap-2 md:w-fit">
            <Button
              className="h-8"
              type="secondary"
              text={t('manage.button.delete')}
              onClick={onClickDelete}
              disabled={!checkedRows.length}
              aria-label="delete sppacecraft"
            />
            <Button
              className="h-8"
              type="blue"
              text={t('manage.button.add')}
              onClick={onClickAddSpacecraft}
              icon={<PlusOutlineIcon className="[&_path]:stroke-white" />}
              aria-label="register a spacecraft"
            />
          </div>
        </div>
        <Grid
          className="max-w-full overflow-y-auto"
            gridColumns={
            (isMobile
              ? manageGridColumns.filter(col => col.showOnMobile)
              : manageGridColumns
            ).map(col => ({
              ...col,
              width: isMobile ? undefined : col.width,  
              title: t(col.title || '') // 250414 그리드
            }))
          }
          gridData={spaceCraftData}
          searchValue={searchValue}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          options={{
            useFilter: false,
            useSearch: {
              use: true,
              searchKeys: ['linkDirection', 'name', 'tleTitle', 'noradId', 'bandName', 'centerFrequency']
            },
            useCheckbox: true,
            useSort: {
              use: true,
              sortKeys: ['linkDirection', 'name', 'tleTitle', 'noradId', 'createdDate', 'bandName', 'centerFrequency']
            },
            usePagination: {
              use: true,
              maxPage: totalPages, // 총 페이지 수 반영
            },
            customCell: {
              // centerFrequency: (_, row) => {
              //   const frequency = (row as SpaceCraftGridData).centerFrequency;
              //   return <span>{frequency !== undefined && frequency !== null ? `${frequency} MHz` : '-'}</span>;
              // },
              viewDetails: (_, row) => (
                <Button
                  text={t('manage.grid.viewDetails')}
                  type="secondary"
                  size="small"
                  onClick={() => onClickDetail(row as SpaceCraftGridData)}
                />
              ),
              update: (_, row) => (
                <Button
                  text={t('manage.grid.update')}
                  type="secondary"
                  size="small"
                  onClick={() => onClickUpdate(row as SpaceCraftGridData)}
                />
              )
            },
          }}
          checkedRows={checkedRows}
          setCheckedRows={setCheckedRows}
        />
      </div>
      <SpaceCraftModal
        data={modalData}
        setData={setModalData}
        onSpaceCraftUpdated={() => getSpaceCraft(currentPage)}
      />
      <RegisterModal
        isOpened={isRegisterModalOpened}
        setIsOpened={setIsRegisterModalOpened}
        onSpaceCraftUpdated={() => getSpaceCraft(currentPage)}
      />
      <DeleteModal
        data={deleteModalData}
        setData={setDeleteModalData}
        onDeleted={() => getSpaceCraft(currentPage)}
        setCheckedRows={setCheckedRows}
      />
      <InfoMessage messageArray={messageArray} onClickDelete={onClickDeleteButton} />
    </>
  );
};

export default ManageSpaceCraft;
